import { useMemo, useState } from "react";
import QRCode from "qrcode";
import { Money } from "@cliqsuite/ui";
import { formatMoney, guestPortalRewardsUrl } from "../lib/format";
import { useStoreConfig } from "../hooks/useStoreConfig";
import { openScanCamera } from "../lib/camera";
import { useBarcodeScanner } from "../hooks/useBarcodeScanner";
import type { Member, Promo, StorePaymentMethod, Voucher } from "@cliqsuite/db/schemas";
import { promoDiscount as promoWorth, promoEligible, voucherDiscount as calcVoucherDiscount, voucherUsable as voucherEligible } from "@cliqsuite/db/schemas";
import type { TenderType } from "../types";
import { useLive } from "../lib/db";
import { CameraBarcodeModal } from "./CameraBarcodeModal";
import "../charge.css";

// Charge (canvas v2-d-charge*, v2-m-charge*): receipt stub on the left, collect on the right.

const TYPE_ICON: Record<StorePaymentMethod["type"], string> = {
  cash: "payments",
  card: "credit_card",
  qr: "qr_code_2",
  bank: "account_balance",
  wallet: "account_balance_wallet",
};

/** core.store_payment_methods.type -> sales.orders.tender_type (bank settles as the closest non-cash/card tender). */
function toTenderType(type: StorePaymentMethod["type"]): TenderType {
  return type === "bank" ? "wallet" : type;
}

export interface ChargePayload {
  tenderType: TenderType;
  methodName: string;
  discount: number;
  member: Member | null;
  promoId: string | null;
  voucherId: string | null;
  /** Points the member pays with; their value is already inside `discount`. */
  pointsRedeemed: number;
  /** A member's receipt goes to their app unless the cashier picks the on-screen QR. */
  receiptOnScreen: boolean;
}

export interface ChargeScreenProps {
  isDesktop: boolean;
  storeId: string;
  /** "#1042", "Table 05" … and what kind of sale, for the receipt stub. */
  title: string;
  subtitle: string;
  subtotal: number;
  /** Staff discount (store-configured). Exclusive with offers and vouchers. */
  employeeDiscountPercent?: number;
  /** Pre-discount tax; only its rate is used, on the discounted subtotal. */
  parentTax: number;
  taxLabel: string;
  currency: { code: string; precision: 0 | 2 | 3 };
  /** Loyalty, vouchers and offers only exist with the crm module. */
  hasCrm: boolean;
  /** Points earned per 1,000 MMK — 0 while the program is paused. */
  loyaltyEarnRate: number;
  earnOnDiscounted: boolean;
  /** Points that take 1,000 off the bill; 0 = paying with points is off. */
  redeemRate: number;
  lines: Array<{ name: string; unitPrice: number; qty: number }>;
  /** An online order: its price was set when the guest placed it, so the register only takes the
   * money. No member, offer, voucher or points here; `fee` is its delivery fee. */
  fixed?: { discount: number; fee: number };
  onBack: () => void;
  onConfirm: (payload: ChargePayload) => Promise<void>;
}



const initials = (name: string) =>
  name
    .split(" ")
    .map((p) => p[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

const Icon = ({ name, size = 20, color, fill }: { name: string; size?: number; color?: string; fill?: boolean }) => (
  <span className="ms" aria-hidden="true" style={{ fontSize: size, color, fontVariationSettings: fill ? "'FILL' 1" : undefined }}>
    {name}
  </span>
);

export function ChargeScreen(props: ChargeScreenProps) {
  const { storeId, subtotal, lines, currency, fixed } = props;
  const hasCrm = props.hasCrm && !fixed;
  const employeeDiscountPercent = fixed ? 0 : (props.employeeDiscountPercent ?? 0);
  const crm = hasCrm ? 1 : 0;
  const { data: promos } = useLive("promos", "SELECT * FROM promos WHERE store_id = $1 AND is_active = true AND $2", [storeId, crm]);
  // Wait for a real sync before trusting "no methods", or the empty state flashes on a cold start.
  const { data: allMethods, isReady: methodsReady } = useLive(
    "store_payment_methods",
    "SELECT * FROM store_payment_methods WHERE store_id = $1 AND is_enabled = true ORDER BY sort_order",
    [storeId],
  );

  const [member, setMember] = useState<Member | null>(null);
  const [receiptOnScreen, setReceiptOnScreen] = useState(false);
  const [scanning, setScanning] = useState<{ stream: MediaStream | null; error?: string } | null>(null);
  const [lookup, setLookup] = useState("");
  const [lookupErr, setLookupErr] = useState("");
  const [promoCode, setPromoCode] = useState("");
  const [promoErr, setPromoErr] = useState("");
  const [codePromoId, setCodePromoId] = useState<string | null>(null);
  // "none" = the cashier chose not to use a voucher; null = nothing picked yet.
  const [voucherId, setVoucherId] = useState<string | null>(null);
  const [usePoints, setUsePoints] = useState(false);
  const [methodId, setMethodId] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [chargeError, setChargeError] = useState("");
  // A customer with no card yet: the scan window can show a QR of the store's rewards page instead
  // (canvas v2-d-charge-join). They join on their own phone, then the cashier scans the new card.
  const storeName = useStoreConfig(storeId).name;
  const joinUrl = guestPortalRewardsUrl(storeId);
  const [joinQr, setJoinQr] = useState<string | null>(null);
  const showJoin = () => joinUrl && void QRCode.toDataURL(joinUrl, { width: 220, margin: 1, color: { dark: "#20242E", light: "#ffffff" } }).then(setJoinQr);

  const { data: memberVouchers } = useLive("vouchers", "SELECT * FROM vouchers WHERE member_id = $1 AND store_id = $2 AND status = 'active'", [member?.id ?? "", storeId]);
  const { data: memberPoints } = useLive("member_store_points", "SELECT * FROM member_store_points WHERE member_id = $1 AND store_id = $2", [member?.id ?? "", storeId]);
  const { data: members } = useLive("members", "SELECT * FROM members WHERE $1 AND deleted_at IS NULL", [crm]);

  const fmt = (v: number) => formatMoney(v, { currencyCode: currency.code, precision: currency.precision });

  // An applied code wins while still eligible; otherwise the best code-less offer applies on its own.
  const codePromo = codePromoId ? (promos.find((p) => p.id === codePromoId) ?? null) : null;
  const autoPromo = useMemo(() => {
    let best: Promo | null = null;
    let bestDiscount = 0;
    for (const p of promos) {
      if (p.code || !promoEligible(p, subtotal)) continue;
      const d = promoWorth(p, subtotal, lines);
      if (d > bestDiscount) {
        best = p;
        bestDiscount = d;
      }
    }
    return best;
  }, [promos, subtotal, lines]);
  const staffDiscountOn = employeeDiscountPercent > 0;
  const employeeDiscount = Math.min(subtotal, Math.round((subtotal * employeeDiscountPercent) / 100));
  // Paying with points: whole blocks of the store's rate, as many as the balance and the bill allow.
  // It stands alone, so an offer or voucher steps aside while it is on.
  const pointBlocks = member && props.redeemRate > 0 ? Math.min(Math.floor((memberPoints[0]?.points ?? 0) / props.redeemRate), Math.floor(subtotal / 1000)) : 0;
  const pointsOn = usePoints && pointBlocks > 0 && !staffDiscountOn;
  const pointsUsed = pointsOn ? pointBlocks * props.redeemRate : 0;
  const pointsDiscount = pointsOn ? pointBlocks * 1000 : 0;
  const offer = codePromo && promoEligible(codePromo, subtotal) ? codePromo : autoPromo;
  const activePromo = staffDiscountOn || pointsOn ? null : offer;
  const promoDiscount = activePromo ? Math.min(promoWorth(activePromo, subtotal, lines), subtotal) : 0;
  const afterPromo = Math.max(0, subtotal - promoDiscount);

  const vouchers = memberVouchers.filter((v) => voucherEligible(v, afterPromo));
  const voucher = !staffDiscountOn && !pointsOn && voucherId && voucherId !== "none" ? (vouchers.find((v) => v.id === voucherId) ?? null) : null;
  const voucherDiscount = voucher ? Math.min(calcVoucherDiscount(voucher, afterPromo), afterPromo) : 0;

  const discount = fixed ? fixed.discount : employeeDiscount + promoDiscount + voucherDiscount + pointsDiscount;
  const discounted = Math.max(0, subtotal - discount);
  const taxRate = subtotal > 0 ? props.parentTax / subtotal : 0;
  // A fixed order's tax was already worked out on its discounted subtotal.
  const tax = fixed ? props.parentTax : Math.round(discounted * taxRate);
  const total = discounted + tax + (fixed?.fee ?? 0);
  const earn = member && (props.earnOnDiscounted || discount === 0) ? Math.floor((total / 1000) * props.loyaltyEarnRate) : 0;

  const methods = allMethods;
  const method = methods.find((m) => m.id === methodId);

  function attach(code: string): boolean {
    const q = code.trim().toLowerCase();
    const digits = q.replace(/[^\d]/g, "");
    const found = members.find(
      (m) => m.member_number.toLowerCase() === q || m.id === q || (digits.length >= 6 && (m.phone ?? "").replace(/[^\d]/g, "").endsWith(digits)),
    );
    if (!found) return false;
    setMember(found);
    setVoucherId(null);
    setUsePoints(false);
    return true;
  }

  // A handheld scanner reads the member card straight into this screen.
  useBarcodeScanner((code) => {
    if (hasCrm && !member) attach(code);
  }, !scanning);

  function applyPromo() {
    if (staffDiscountOn) {
      setPromoErr("Remove the staff discount before applying a voucher or offer.");
      return;
    }
    const code = promoCode.trim().toUpperCase();
    if (!code) return;
    const promo = promos.find((p) => (p.code ?? "").toUpperCase() === code);
    if (!promo || !promoEligible(promo, subtotal)) {
      setPromoErr("Invalid or ineligible code");
      return;
    }
    setCodePromoId(promo.id);
    setPromoCode("");
    setPromoErr("");
  }

  async function confirm() {
    if (!method || submitting) return;
    setSubmitting(true);
    setChargeError("");
    try {
      await props.onConfirm({ tenderType: toTenderType(method.type), methodName: method.name, discount, member, promoId: activePromo?.id ?? null, voucherId: voucher?.id ?? null, pointsRedeemed: pointsUsed, receiptOnScreen });
    } catch {
      setChargeError("Payment couldn't be confirmed. Check the connection and try again.");
    } finally {
      setSubmitting(false);
    }
  }

  const stub = (
    <div className="pos-stub">
      <div aria-hidden="true" className="pos-zig top" />
      <div className="pos-stub-body">
        <div className="pos-stub-head">
          <b>{props.title}</b>
          <span>{props.subtitle}</span>
        </div>
        <div className="pos-stub-lines">
          {lines.map((l, i) => (
            <div key={i}>
              <span className="num">{l.qty}×</span>
              <span>{l.name}</span>
              <span className="num">{fmt(l.unitPrice * l.qty)}</span>
            </div>
          ))}
        </div>
        <div className="pos-stub-sums">
          <div>
            <span>Subtotal</span>
            <span className="num">{fmt(subtotal)}</span>
          </div>
          {fixed && fixed.discount > 0 && (
            <div className="off">
              <span>Discount</span>
              <span className="num">−{fmt(fixed.discount)}</span>
            </div>
          )}
          {fixed && fixed.fee > 0 && (
            <div>
              <span>Delivery</span>
              <span className="num">{fmt(fixed.fee)}</span>
            </div>
          )}
          {employeeDiscount > 0 && (
            <div className="off">
              <span>Staff discount {employeeDiscountPercent}%</span>
              <span className="num">−{fmt(employeeDiscount)}</span>
            </div>
          )}
          {activePromo && promoDiscount > 0 && (
            <div className="off">
              <span>{activePromo.name}</span>
              <span className="num">−{fmt(promoDiscount)}</span>
            </div>
          )}
          {voucher && voucherDiscount > 0 && (
            <div className="off">
              <span>{voucher.name}</span>
              <span className="num">−{fmt(voucherDiscount)}</span>
            </div>
          )}
          {pointsOn && (
            <div className="off">
              <span>{pointsUsed.toLocaleString()} points</span>
              <span className="num">−{fmt(pointsDiscount)}</span>
            </div>
          )}
          <div>
            <span>{props.taxLabel}</span>
            <span className="num">{fmt(tax)}</span>
          </div>
          <div className="total">
            <span>Total</span>
            <span className="num">{fmt(total)}</span>
          </div>
        </div>
      </div>
      <div aria-hidden="true" className="pos-zig bottom" />
    </div>
  );

  const vouchersBlocked = staffDiscountOn && (!!offer || vouchers.length > 0 || !!promoCode);

  return (
    <div className={`pos-charge-screen${props.isDesktop ? "" : " phone"}`}>
      {props.isDesktop && (
        <aside className="pos-charge-left">
          <button className="pos-back" onClick={props.onBack} disabled={submitting}>
            <Icon name="chevron_left" size={22} />
            Back to ticket
          </button>
          {stub}
        </aside>
      )}
      <section className="pos-charge-right">
        {!props.isDesktop && (
          <button className="pos-back" onClick={props.onBack} disabled={submitting}>
            <Icon name="chevron_left" size={22} />
            Ticket
          </button>
        )}
        <div className="pos-collect">
          <div>To collect</div>
          <div><Money amount={total} opts={{ currencyCode: currency.code, precision: currency.precision }} /></div>
        </div>

        {hasCrm &&
          (member ? (
            <div className="pos-member">
              <div className="pos-member-av" style={{ background: member.avatar_color ?? "var(--blue)" }}>
                {initials(member.name)}
              </div>
              <div>
                <b>{member.name}</b>
                <small>
                  {(memberPoints[0]?.points ?? 0).toLocaleString()} pts
                  {pointsOn ? ` · ${pointsUsed.toLocaleString()} used, ${((memberPoints[0]?.points ?? 0) - pointsUsed).toLocaleString()} left` : ""}
                  {earn > 0 ? (pointsOn ? ` · earns +${earn}` : ` · earns +${earn} on this sale`) : ""}
                </small>
              </div>
              <button className="pos-sq-btn big" aria-label="Remove member" onClick={() => setMember(null)}>
                <Icon name="person_remove" size={19} color="var(--text2)" />
              </button>
            </div>
          ) : (
            <button
              className="pos-scan-member"
              onClick={async () => {
                setLookup("");
                setLookupErr("");
                setScanning(await openScanCamera());
              }}
            >
              <span>
                <Icon name="qr_code_scanner" size={24} color="var(--coral)" />
              </span>
              <span>
                <b>Scan member card</b>
                {props.isDesktop && <small>Points and vouchers apply on their own. The handheld scanner works too.</small>}
              </span>
              <Icon name="chevron_right" size={24} color="var(--muted)" />
            </button>
          ))}
        {hasCrm && !member && !scanning && lookupErr && (
          <div role="alert" className="pos-field-err">
            <Icon name="error" size={16} />
            {lookupErr}
          </div>
        )}

        {hasCrm && (
          <div className="pos-charge-extras">
            {staffDiscountOn && vouchersBlocked && (
              <div role="alert" className="pos-alert warn">
                <Icon name="block" size={18} color="var(--amber)" />
                Remove the staff discount before applying a voucher or offer.
              </div>
            )}
            {offer && (
              <div className={`pos-offer${staffDiscountOn || pointsOn ? " off" : ""}`}>
                <Icon name="sell" size={18} />
                <span>{offer.name}</span>
                <small>{staffDiscountOn || pointsOn ? "Not applied" : `−${fmt(promoDiscount)}`}</small>
              </div>
            )}
            {member && vouchers.length > 0 && (
              <div className="pos-field">
                <label>LOYALTY VOUCHER</label>
                <div role="radiogroup" aria-label="Loyalty voucher" className="pos-vouchers">
                  {vouchers.map((v) => (
                    <button
                      key={v.id}
                      role="radio"
                      aria-checked={voucherId === v.id}
                      disabled={staffDiscountOn}
                      className={voucherId === v.id ? "on" : ""}
                      onClick={() => setVoucherId(v.id)}
                    >
                      <b>
                        <Icon name={v.discount_type === "percentage" ? "percent" : "redeem"} size={17} />
                        {v.name}
                      </b>
                      <small>{v.discount_type === "percentage" ? `${v.discount_value}% off` : `Up to ${fmt(v.discount_value)}`}</small>
                    </button>
                  ))}
                  <button role="radio" aria-checked={voucherId === "none" || voucherId === null} className={voucherId === "none" || voucherId === null ? "on none" : ""} onClick={() => setVoucherId("none")}>
                    <b>
                      <Icon name="block" size={17} />
                      Don't use
                    </b>
                  </button>
                </div>
              </div>
            )}
            {member && pointBlocks > 0 && (
              <div className="pos-field">
                <label>PAY WITH POINTS</label>
                <div role="radiogroup" aria-label="Pay with points" className="pos-vouchers">
                  <button role="radio" aria-checked={pointsOn} disabled={staffDiscountOn} className={pointsOn ? "on" : ""} onClick={() => setUsePoints(true)}>
                    <b>
                      <Icon name="loyalty" size={17} />
                      Use {(pointBlocks * props.redeemRate).toLocaleString()} pts
                    </b>
                    <small>
                      {fmt(pointBlocks * 1000)} off, {((memberPoints[0]?.points ?? 0) - pointBlocks * props.redeemRate).toLocaleString()} pts left
                    </small>
                  </button>
                  <button role="radio" aria-checked={!pointsOn} className={pointsOn ? "" : "on none"} onClick={() => setUsePoints(false)}>
                    <b>
                      <Icon name="block" size={17} />
                      Don't use
                    </b>
                    <small>
                      {props.redeemRate.toLocaleString()} pts = {fmt(1000)}
                    </small>
                  </button>
                </div>
              </div>
            )}
            <div className="pos-field">
              <label htmlFor="promo">PROMO CODE</label>
              <div className="pos-promo">
                <span className={`pos-input${promoErr ? " err" : ""}`}>
                  <Icon name="confirmation_number" size={19} color="var(--text2)" />
                  <input
                    id="promo"
                    placeholder={codePromo ? `${codePromo.code} applied` : "Have a code?"}
                    value={promoCode}
                    onChange={(e) => {
                      setPromoCode(e.target.value);
                      setPromoErr("");
                    }}
                    onKeyDown={(e) => e.key === "Enter" && applyPromo()}
                  />
                </span>
                <button onClick={applyPromo}>Apply</button>
              </div>
              {promoErr && (
                <div role="alert" className="pos-field-err">
                  <Icon name="error" size={16} />
                  {promoErr}
                </div>
              )}
            </div>
          </div>
        )}

        {methodsReady && methods.length === 0 ? (
          <div role="alert" className="pos-nomethods">
            <Icon name="credit_card_off" size={34} color="var(--muted)" />
            <div>
              <b>No payment methods yet</b>
              <small>Add one in Owner Portal, Payment methods, before taking payment.</small>
            </div>
          </div>
        ) : (
          <div role="radiogroup" aria-label="Payment method" className="pos-methods" style={{ gridTemplateColumns: `repeat(${props.isDesktop ? Math.min(Math.max(methods.length, 3), 5) : 2}, minmax(0, 1fr))` }}>
            {methods.map((m) => (
              <button key={m.id} role="radio" aria-checked={m.id === methodId} className={m.id === methodId ? "on" : ""} onClick={() => setMethodId(m.id)}>
                <Icon name={TYPE_ICON[m.type]} size={28} color={m.id === methodId ? "var(--coral)" : "var(--ink)"} fill={m.id === methodId} />
                <span>{m.name}</span>
                {m.id === methodId && (
                  <span className="pos-method-check">
                    <Icon name="check" size={17} color="#fff" />
                  </span>
                )}
              </button>
            ))}
          </div>
        )}

        {method?.type === "qr" &&
          (method.image_data ? (
            <div className="pos-qr">
              <img src={method.image_data} alt={`${method.name} QR code for ${fmt(total)}`} width={132} height={132} />
              <div>
                <b>Customer scans the store's {method.name} QR</b>
                <p>Scan with the {method.name} app, then show the cashier the success screen.</p>
                <small>QR set in Owner Portal, Payment methods</small>
              </div>
            </div>
          ) : (
            <div role="alert" className="pos-qr missing">
              <Icon name="qr_code_2" size={30} color="var(--amber)" />
              <div>
                <b>No QR set up for {method.name}</b>
                <p>Add one in Owner Portal, Payment methods. Pick another way for now.</p>
              </div>
            </div>
          ))}

        {member && method?.type !== "qr" ? (
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <span style={{ fontSize: 13.5, fontWeight: 800, color: "var(--text2)" }}>Receipt</span>
            <div role="radiogroup" aria-label="Receipt" className="pos-seg">
              <button role="radio" aria-checked={!receiptOnScreen} className={receiptOnScreen ? "" : "on"} style={{ padding: "0 12px" }} onClick={() => setReceiptOnScreen(false)}>
                To {member.name.split(" ")[0]}'s app
              </button>
              <button role="radio" aria-checked={receiptOnScreen} className={receiptOnScreen ? "on" : ""} style={{ padding: "0 12px" }} onClick={() => setReceiptOnScreen(true)}>
                QR on screen
              </button>
            </div>
          </div>
        ) : method?.type !== "qr" && (
          <div className="pos-eco">
            <Icon name="eco" size={19} color="var(--ok)" />
            Receipt is digital. The customer scans a QR on the next screen.
          </div>
        )}
        {chargeError && (
          <div role="alert" className="pos-alert bad">
            <Icon name="wifi_off" size={18} />
            {chargeError}
          </div>
        )}
        <div style={{ flex: 1 }} />
        {method && !(method.type === "qr" && !method.image_data) ? (
          <button className="pos-take" disabled={submitting} onClick={() => void confirm()}>
            <Icon name={submitting ? "progress_activity" : "check_circle"} size={22} color="#fff" />
            {submitting ? "Confirming…" : method.type === "qr" ? "Payment received" : `Take ${fmt(total)}`}
          </button>
        ) : (
          <div className="pos-take idle">
            <Icon name="touch_app" color="var(--muted)" />
            Pick how they're paying
          </div>
        )}
      </section>

      {scanning && (
        <CameraBarcodeModal
          stream={scanning.stream}
          initialError={scanning.error}
          title="Scan member card"
          hint="Hold the card QR or barcode inside the frame"
          onDetected={(code) => {
            if (!attach(code)) setLookupErr("No member with that card");
          }}
          onClose={() => setScanning(null)}
          footer={
            <form
              className="pos-scan-foot"
              onSubmit={(e) => {
                e.preventDefault();
                if (attach(lookup)) setScanning(null);
                else setLookupErr("No member with that number or phone");
              }}
            >
              <span className={`pos-input${lookupErr ? " err" : ""}`}>
                <Icon name="keyboard" size={19} color="var(--text2)" />
                <input
                  aria-label="Member number or phone"
                  placeholder="Or type the member number or phone"
                  value={lookup}
                  onChange={(e) => {
                    setLookup(e.target.value);
                    setLookupErr("");
                  }}
                />
              </span>
              <button type="submit">Find</button>
              {lookupErr && (
                <div role="alert" className="pos-field-err">
                  <Icon name="error" size={16} />
                  {lookupErr}
                </div>
              )}
              <div className="pos-alert soft">
                <Icon name="barcode_reader" size={20} color="var(--text2)" />A handheld scanner picks the card up without this window.
              </div>
              {joinUrl && (
                <button type="button" className="pos-scan-join" onClick={showJoin}>
                  <Icon name="qr_code_2" size={19} />
                  No card yet? Show join code
                </button>
              )}
            </form>
          }
        />
      )}
      {scanning && joinQr && (
        <div className="pos-dim" onClick={() => setJoinQr(null)}>
          <section role="dialog" aria-modal="true" aria-labelledby="join-t" className="pos-dialog pos-approve pos-qr-dialog" onClick={(e) => e.stopPropagation()}>
            <h2 id="join-t">Join {storeName} rewards</h2>
            <p>The customer scans this with their phone camera and joins in one tap. Their new card then shows on their phone: scan it to add them to this sale.</p>
            <img src={joinQr} alt={`QR code to join ${storeName} rewards`} width={220} height={220} />
            <div className="dlg-row">
              <button className="pos-btn-dark" onClick={() => setJoinQr(null)}>
                Scan their card
              </button>
            </div>
          </section>
        </div>
      )}
    </div>
  );
}
